import React, { useState, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Database, FileText, GitBranch, ChevronDown, ChevronRight,
  Type, Hash, Link, List, Star, Users, Zap, Layers,
  AlertTriangle, CircleDot, ArrowRight, Tag, Check, PlusCircle, Pencil, Calendar, ToggleLeft, FileType
} from 'lucide-react';

// ─── Field type badges ───────────────────────────────────────────

const FIELD_TYPE_CONFIG = {
  text: { label: 'Text', icon: Type, color: 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-400' },
  number: { label: 'Number', icon: Hash, color: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  date: { label: 'Date', icon: Calendar, color: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  url: { label: 'URL', icon: Link, color: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  select: { label: 'Select', icon: List, color: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  multi_select: { label: 'Multi-select', icon: Tag, color: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  boolean: { label: 'Boolean', icon: ToggleLeft, color: 'bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  user: { label: 'User', icon: Users, color: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400' },
  object_relationship: { label: 'Relation', icon: ArrowRight, color: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  rich_text: { label: 'Rich Text', icon: FileType, color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
};

function FieldTypeBadge({ type }) {
  const config = FIELD_TYPE_CONFIG[type] || { label: type, icon: CircleDot, color: 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[0.625rem] font-medium leading-none ${config.color}`}>
      <Icon className="w-2.5 h-2.5" />
      {config.label}
    </span>
  );
}

// ─── Object Types (CRM) ─────────────────────────────────────────

function ObjectTypeCard({ objectType }) {
  const [expanded, setExpanded] = useState(false);
  const { name, slug, fields = [] } = objectType;
  const requiredFields = fields.filter(f => f.required);
  const optionalFields = fields.filter(f => !f.required);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
      >
        <div className="w-6 h-6 rounded-md bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0">
          <Database className="w-3 h-3 text-brand-600 dark:text-brand-400" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{name}</span>
          <span className="ml-1.5 text-[0.625rem] text-gray-400 dark:text-gray-500 font-mono">{slug}</span>
        </div>
        <span className="text-[0.625rem] text-gray-400 dark:text-gray-500 flex-shrink-0">
          {fields.length} field{fields.length !== 1 ? 's' : ''}
        </span>
        {expanded
          ? <ChevronDown className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          : <ChevronRight className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        }
      </button>
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700/60">
          {requiredFields.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <span className="text-[0.5625rem] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Required</span>
            </div>
          )}
          {requiredFields.map(field => (
            <FieldRow key={field.id} field={field} />
          ))}
          {optionalFields.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <span className="text-[0.5625rem] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Optional</span>
            </div>
          )}
          {optionalFields.map(field => (
            <FieldRow key={field.id} field={field} />
          ))}
        </div>
      )}
    </div>
  );
}

function FieldRow({ field }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <span className="text-[0.6875rem] text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">
        {field.name}
        {field.required && <Star className="w-2 h-2 text-amber-400 dark:text-amber-500 inline ml-1 -mt-0.5" />}
      </span>
      <span className="text-[0.5625rem] text-gray-400 dark:text-gray-500 font-mono flex-shrink-0 hidden sm:inline">
        {field.slug}
      </span>
      <FieldTypeBadge type={field.type} />
    </div>
  );
}

function ObjectTypesResult({ data }) {
  const objectTypes = data.object_types || [];
  return (
    <div className="space-y-1.5">
      {objectTypes.map(ot => (
        <ObjectTypeCard key={ot.id} objectType={ot} />
      ))}
    </div>
  );
}

// ─── Forms ───────────────────────────────────────────────────────

function FormsResult({ data }) {
  const forms = data.forms || [];
  if (forms.length === 0) {
    return <EmptyState message={data.message || 'No forms found.'} />;
  }
  return (
    <div className="space-y-1">
      {forms.map(form => (
        <div key={form.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
          <div className="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1 min-w-0 truncate">
            {form.name}
          </span>
          <StatusPill status={form.status} />
        </div>
      ))}
      {data.total_count > data.count && (
        <TruncationNote shown={data.count} total={data.total_count} />
      )}
    </div>
  );
}

// ─── Workflows ───────────────────────────────────────────────────

function WorkflowsResult({ data }) {
  const workflows = data.workflows || [];
  if (workflows.length === 0) {
    return <EmptyState message="No workflows found." />;
  }
  return (
    <div className="space-y-1">
      {workflows.map(wf => (
        <div key={wf.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
          <div className="w-6 h-6 rounded-md bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0">
            <GitBranch className="w-3 h-3 text-brand-600 dark:text-brand-400" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate block">
              {wf.name}
            </span>
            <span className="text-[0.625rem] text-gray-400 dark:text-gray-500">
              {wf.node_count} node{wf.node_count !== 1 ? 's' : ''}
            </span>
          </div>
          <StatusPill status={wf.status} />
        </div>
      ))}
    </div>
  );
}

// ─── Node Types ──────────────────────────────────────────────────

function NodeTypesResult({ data }) {
  const triggers = data.triggers || [];
  const actions = data.actions || [];

  // Group actions by subcategory
  const groupedActions = {};
  actions.forEach(action => {
    const group = action.subcategory || 'Other';
    if (!groupedActions[group]) groupedActions[group] = [];
    groupedActions[group].push(action);
  });

  return (
    <div className="space-y-3">
      {triggers.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Zap className="w-3 h-3 text-amber-500 dark:text-amber-400" />
            <span className="text-[0.625rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Triggers ({triggers.length})
            </span>
          </div>
          <div className="space-y-1">
            {triggers.map(t => (
              <NodeTypeRow key={t.type} node={t} variant="trigger" />
            ))}
          </div>
        </div>
      )}
      {Object.entries(groupedActions).map(([group, groupActions]) => (
        <div key={group}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Layers className="w-3 h-3 text-brand-500 dark:text-brand-400" />
            <span className="text-[0.625rem] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {group} ({groupActions.length})
            </span>
          </div>
          <div className="space-y-1">
            {groupActions.map(a => (
              <NodeTypeRow key={a.type} node={a} variant="action" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NodeTypeRow({ node, variant }) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
        variant === 'trigger'
          ? 'bg-amber-400 dark:bg-amber-500'
          : 'bg-brand-400 dark:bg-brand-500'
      }`} />
      <div className="flex-1 min-w-0">
        <span className="text-[0.6875rem] font-medium text-gray-800 dark:text-gray-200 block">
          {node.label}
        </span>
        {node.description && (
          <span className="text-[0.625rem] text-gray-400 dark:text-gray-500 block truncate">
            {node.description}
          </span>
        )}
      </div>
      <span className="text-[0.5625rem] text-gray-400 dark:text-gray-500 font-mono flex-shrink-0 mt-0.5">
        {node.type}
      </span>
    </div>
  );
}

// ─── Workflow Summary ────────────────────────────────────────────

function WorkflowSummaryResult({ data }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
        <div className="w-6 h-6 rounded-md bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center flex-shrink-0">
          <GitBranch className="w-3 h-3 text-brand-600 dark:text-brand-400" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 block truncate">{data.name}</span>
          {data.description && (
            <span className="text-[0.625rem] text-gray-400 dark:text-gray-500 block truncate">{data.description}</span>
          )}
        </div>
        <StatusPill status={data.status} />
      </div>
      {data.nodes && data.nodes.length > 0 && (
        <div className="space-y-1">
          {data.nodes.map((node, i) => (
            <div key={node.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30">
              <span className="text-[0.625rem] text-gray-400 dark:text-gray-500 font-mono w-4 text-right flex-shrink-0">{i + 1}</span>
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                node.category === 'trigger'
                  ? 'bg-amber-400 dark:bg-amber-500'
                  : 'bg-brand-400 dark:bg-brand-500'
              }`} />
              <span className="text-[0.6875rem] font-medium text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">
                {node.label}
              </span>
              <span className="text-[0.5625rem] text-gray-400 dark:text-gray-500 font-mono flex-shrink-0">
                {node.type_label || node.type}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 px-3 py-1.5 text-[0.625rem] text-gray-400 dark:text-gray-500">
        <span>{data.node_count} node{data.node_count !== 1 ? 's' : ''}</span>
        <span>{data.edge_count} connection{data.edge_count !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}

// ─── CRM Field Results ───────────────────────────────────────────

function FieldResultCard({ data, action }) {
  const field = data.field || {};
  const objectName = data.object_name || 'Object';
  const changes = data.changes || [];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
          action === 'add'
            ? 'bg-green-50 dark:bg-green-900/30'
            : 'bg-blue-50 dark:bg-blue-900/30'
        }`}>
          {action === 'add'
            ? <PlusCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
            : <Pencil className="w-3 h-3 text-blue-600 dark:text-blue-400" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 block">
            {field.name || 'Field'}
          </span>
          <span className="text-[0.625rem] text-gray-400 dark:text-gray-500">
            on {objectName}
            {field.slug && <span className="ml-1 font-mono">({field.slug})</span>}
          </span>
        </div>
        <FieldTypeBadge type={field.field_type} />
        {field.is_required && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.5625rem] font-medium leading-none bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0">
            Required
          </span>
        )}
      </div>
      {field.options?.choices && field.options.choices.length > 0 && (
        <div className="px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30">
          <span className="text-[0.5625rem] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Choices</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {field.options.choices.map((choice, i) => (
              <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.625rem] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-400">
                {choice}
              </span>
            ))}
          </div>
        </div>
      )}
      {changes.length > 0 && (
        <div className="px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30">
          <span className="text-[0.5625rem] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Changes</span>
          <div className="mt-1 space-y-0.5">
            {changes.map((change, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[0.6875rem] text-gray-600 dark:text-gray-400">
                <Check className="w-2.5 h-2.5 text-green-500 dark:text-green-400 flex-shrink-0" />
                <span className="capitalize">{change}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared UI elements ──────────────────────────────────────────

function StatusPill({ status }) {
  if (!status) return null;
  const colors = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
    published: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-500 dark:bg-gray-700/50 dark:text-gray-500',
  };
  const colorClass = colors[status] || colors.draft;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[0.5625rem] font-medium leading-none capitalize flex-shrink-0 ${colorClass}`}>
      {status}
    </span>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-[0.6875rem] text-gray-400 dark:text-gray-500">
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      {message}
    </div>
  );
}

function TruncationNote({ shown, total }) {
  return (
    <div className="px-3 py-1 text-[0.625rem] text-gray-400 dark:text-gray-500 italic">
      Showing {shown} of {total} results
    </div>
  );
}

function ErrorResult({ error }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20">
      <AlertTriangle className="w-3 h-3 text-red-500 dark:text-red-400 flex-shrink-0" />
      <span className="text-[0.6875rem] text-red-600 dark:text-red-400">{error}</span>
    </div>
  );
}

// ─── Raw JSON fallback ───────────────────────────────────────────

function RawOutput({ output }) {
  return (
    <pre className="mt-1 bg-gray-100 dark:bg-gray-800/80 rounded-md px-2.5 py-2 text-gray-700 dark:text-gray-300 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap text-xs">
      {output}
    </pre>
  );
}

// ─── Render type detection ───────────────────────────────────────

/**
 * Determines how to render the output: 'structured', 'generic', 'error',
 * 'raw_json', or 'raw_text'. Also parses the JSON data if possible.
 */
function detectRenderType(toolName, output) {
  if (!output) return { type: 'empty', data: null };

  let data;
  try {
    data = typeof output === 'string' ? JSON.parse(output) : output;
  } catch {
    return { type: 'raw_text', data: null };
  }

  if (data.success === false && data.error) {
    return { type: 'error', data };
  }

  switch (toolName) {
    case 'list_user_object_definitions':
      if (data.object_types) return { type: 'structured', data };
      break;
    case 'list_user_forms':
      if (data.forms) return { type: 'structured', data };
      break;
    case 'list_user_workflows':
      if (data.workflows) return { type: 'structured', data };
      break;
    case 'list_available_node_types':
      if (data.triggers || data.actions) return { type: 'structured', data };
      break;
    case 'get_workflow_summary':
      if (data.name !== undefined) return { type: 'structured', data };
      break;
    case 'add_field_to_object':
    case 'edit_field_on_object':
      if (data.field) return { type: 'structured', data };
      break;
    default:
      break;
  }

  // Check for generic list pattern
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (Array.isArray(val) && val.length > 0 && val[0].id && val[0].name) {
      return { type: 'generic', data };
    }
  }

  return { type: 'raw_json', data };
}

// ─── Main renderer ───────────────────────────────────────────────

/**
 * Renders a structured UI for known tool result shapes, with a fallback
 * to raw JSON/text for unrecognized tools or data shapes.
 * Fires PostHog events to track structured vs. fallback rendering.
 */
export default function ToolCallResultRenderer({ toolName, output }) {
  const posthog = usePostHog();
  const trackedRef = useRef(false);

  const { type: renderType, data } = detectRenderType(toolName, output);

  useEffect(() => {
    if (trackedRef.current || renderType === 'empty') return;
    trackedRef.current = true;

    if (renderType === 'structured' || renderType === 'generic') {
      posthog?.capture('chat_tool_result_viewed', {
        tool_name: toolName,
        render_type: renderType,
        item_count: data?.count ?? data?.total_count ?? null,
      });
    } else if (renderType === 'raw_json' || renderType === 'raw_text') {
      posthog?.capture('chat_tool_result_fallback', {
        tool_name: toolName,
        render_type: renderType,
      });
    }
  }, [renderType, toolName, data, posthog]);

  if (renderType === 'empty') return null;

  if (renderType === 'raw_text') {
    return <RawOutput output={output} />;
  }

  if (renderType === 'error') {
    return <ErrorResult error={data.error} />;
  }

  if (renderType === 'structured') {
    return (
      <div className="mt-1.5">
        <StructuredResult toolName={toolName} data={data} />
      </div>
    );
  }

  if (renderType === 'generic') {
    return <div className="mt-1.5">{tryGenericListRender(data)}</div>;
  }

  // raw_json
  return <RawOutput output={typeof output === 'string' ? output : JSON.stringify(output, null, 2)} />;
}

/**
 * Routes to the correct structured sub-renderer based on tool name.
 */
function StructuredResult({ toolName, data }) {
  switch (toolName) {
    case 'list_user_object_definitions': return <ObjectTypesResult data={data} />;
    case 'list_user_forms': return <FormsResult data={data} />;
    case 'list_user_workflows': return <WorkflowsResult data={data} />;
    case 'list_available_node_types': return <NodeTypesResult data={data} />;
    case 'get_workflow_summary': return <WorkflowSummaryResult data={data} />;
    case 'add_field_to_object': return <FieldResultCard data={data} action="add" />;
    case 'edit_field_on_object': return <FieldResultCard data={data} action="edit" />;
    default: return null;
  }
}

/**
 * Generic fallback: if the data has an array property with objects that have
 * id+name, render them as a simple list. This handles future tools without
 * needing explicit support.
 */
function tryGenericListRender(data) {
  // Find the first array property with objects that have id+name
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (Array.isArray(val) && val.length > 0 && val[0].id && val[0].name) {
      return (
        <div className="space-y-1">
          {val.map(item => (
            <div key={item.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-800/50">
              <div className="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center flex-shrink-0">
                <CircleDot className="w-3 h-3 text-gray-500 dark:text-gray-400" />
              </div>
              <span className="text-xs font-medium text-gray-800 dark:text-gray-200 flex-1 min-w-0 truncate">
                {item.name}
              </span>
              {item.status && <StatusPill status={item.status} />}
            </div>
          ))}
          {data.total_count > (data.count || val.length) && (
            <TruncationNote shown={data.count || val.length} total={data.total_count} />
          )}
        </div>
      );
    }
  }

  return null;
}
